import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, Target, Inbox, ListTodo, Users, Search, GraduationCap,
  FolderKanban, CalendarDays, LayoutDashboard, GanttChartSquare,
  Clock, Settings, Sparkles, Handshake, ChevronRight, CalendarRange, Wrench,
  UserCircle, Compass, PiggyBank, Banknote,
} from 'lucide-react';
import { T, ROLES, springs } from '../../lib/tokens';

const NAV = [
  { id: 'today',   icon: Home,        label: 'Today'  },
  { id: 'kay',     icon: UserCircle,  label: 'Kay'    },
  { id: 'saul',    icon: Compass,     label: 'Saul'   },
  { id: 'penny',   icon: PiggyBank,   label: 'Penny'  },
  { id: 'finance', icon: Banknote,    label: 'Money'  },
  { id: 'briefs',  icon: Sparkles,    label: 'Briefs' },
  { id: 'inbox',   icon: Inbox,       label: 'Inbox'  },
  {
    group: 'plan',
    icon: CalendarRange,
    label: 'Plan',
    defaultOpen: true,
    items: [
      { id: 'goals',    icon: Target,           label: 'Goals'    },
      { id: 'projects', icon: FolderKanban,     label: 'Projects' },
      { id: 'promises', icon: Handshake,        label: 'Promises' },
      { id: 'tasks',    icon: ListTodo,         label: 'Tasks'    },
      { id: 'calendar', icon: CalendarDays,     label: 'Calendar' },
      { id: 'gantt',    icon: GanttChartSquare, label: 'Timeline' },
    ],
  },
  {
    group: 'tools',
    icon: Wrench,
    label: 'Tools',
    defaultOpen: false,
    items: [
      { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { id: 'agents',    icon: Users,           label: 'Agents'    },
      { id: 'research',  icon: Search,          label: 'Research'  },
      { id: 'timetrack', icon: Clock,           label: 'Time'      },
      { id: 'mentoring', icon: GraduationCap,   label: 'Mentoring' },
    ],
  },
  { id: 'settings', icon: Settings, label: 'Settings' },
];

const findGroupForView = (view) => NAV.find(it => it.group && it.items.some(s => s.id === view))?.group;

const NavLeaf = ({ Icon, label, active, onClick, indent, badge }) => (
  <motion.button
    onClick={onClick}
    whileHover={!active ? { background: 'rgba(255,255,255,0.55)' } : {}}
    whileTap={{ scale: 0.97 }}
    transition={springs.snappy}
    style={{
      display: 'flex', alignItems: 'center', gap: 11,
      width: '100%', padding: '9px 12px',
      paddingLeft: indent ? 26 : 12,
      borderRadius: 12, border: 'none', cursor: 'pointer', marginBottom: 2,
      background: active ? 'rgba(255,255,255,0.78)' : 'transparent',
      color: active ? T.amber : T.textMid,
      fontSize: 14, fontWeight: active ? 600 : 500,
      fontFamily: 'inherit',
      position: 'relative',
      boxShadow: active ? '0 2px 8px rgba(60,40,90,0.06), inset 0 1px 0 rgba(255,255,255,0.7)' : 'none',
    }}
  >
    <span style={{
      width: indent ? 22 : 26, height: indent ? 22 : 26, borderRadius: 7,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: active ? T.amberLight : 'transparent',
      transition: 'background 0.2s',
      flexShrink: 0,
    }}>
      <Icon size={indent ? 14 : 15} />
    </span>
    <span style={{ flex: 1, textAlign: 'left' }}>{label}</span>
    {badge}
  </motion.button>
);

export const Sidebar = ({ view, onNav, agents, unreadCount, ventureBalance }) => {
  const [openGroups, setOpenGroups] = useState(() => {
    const init = {};
    NAV.forEach(it => { if (it.group) init[it.group] = !!it.defaultOpen; });
    return init;
  });

  // When the active view lives inside a collapsed group, expand it so the user
  // can see where they are. Doesn't override a group the user has explicitly
  // closed unless they then navigate into it.
  useEffect(() => {
    const g = findGroupForView(view);
    if (g) setOpenGroups(s => (s[g] ? s : { ...s, [g]: true }));
  }, [view]);

  const toggleGroup = (g) => setOpenGroups(s => ({ ...s, [g]: !s[g] }));

  return (
    <div style={{
      width: 232, height: '100vh', flexShrink: 0,
      background: 'rgba(255,255,255,0.45)',
      backdropFilter: 'blur(40px) saturate(200%)',
      WebkitBackdropFilter: 'blur(40px) saturate(200%)',
      borderRight: '1px solid rgba(255,255,255,0.40)',
      boxShadow: 'inset -1px 0 0 rgba(255,255,255,0.30)',
      display: 'flex', flexDirection: 'column',
      padding: '22px 14px',
    }}>
      {/* Logo */}
      <div style={{ padding: '4px 10px 22px', borderBottom: '1px solid rgba(0,0,0,0.06)', marginBottom: 14 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: T.text, letterSpacing: '-0.035em' }}>
          Exec HQ
        </div>
        <div style={{ fontSize: 11, color: T.textMuted, marginTop: 3, fontWeight: 400 }}>
          Mike Pryke
        </div>
      </div>

      {/* Nav */}
      <nav style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(0,0,0,0.15) transparent',
        marginRight: -6,
        paddingRight: 6,
      }}>
        {NAV.map(item => {
          if (item.group) {
            const open = openGroups[item.group];
            const Icon = item.icon;
            const childActive = item.items.some(s => s.id === view);
            return (
              <div key={item.group}>
                <motion.button
                  onClick={() => toggleGroup(item.group)}
                  whileHover={{ background: 'rgba(255,255,255,0.45)' }}
                  whileTap={{ scale: 0.97 }}
                  transition={springs.snappy}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 11,
                    width: '100%', padding: '9px 12px', borderRadius: 12,
                    border: 'none', cursor: 'pointer', marginBottom: 2,
                    background: 'transparent',
                    color: childActive && !open ? T.amber : T.textMid,
                    fontSize: 14, fontWeight: 500,
                    fontFamily: 'inherit',
                  }}
                >
                  <span style={{
                    width: 26, height: 26, borderRadius: 7,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'transparent', flexShrink: 0,
                  }}>
                    <Icon size={15} />
                  </span>
                  <span style={{ flex: 1, textAlign: 'left' }}>{item.label}</span>
                  <motion.span
                    animate={{ rotate: open ? 90 : 0 }}
                    transition={springs.snappy}
                    style={{ display: 'flex', color: T.textLight }}
                  >
                    <ChevronRight size={14} />
                  </motion.span>
                </motion.button>
                <AnimatePresence initial={false}>
                  {open && (
                    <motion.div
                      key={item.group + '-content'}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.18 }}
                      style={{ overflow: 'hidden' }}
                    >
                      {item.items.map(sub => (
                        <NavLeaf
                          key={sub.id}
                          Icon={sub.icon}
                          label={sub.label}
                          active={view === sub.id}
                          onClick={() => onNav(sub.id)}
                          indent
                        />
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          }
          const badge = item.id === 'inbox' && unreadCount > 0 ? (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={springs.bouncy}
              style={{
                background: T.amber, color: '#fff',
                borderRadius: 10, fontSize: 10, fontWeight: 700,
                padding: '1px 6px', minWidth: 18, textAlign: 'center',
              }}
            >
              {unreadCount}
            </motion.span>
          ) : null;
          return (
            <NavLeaf
              key={item.id}
              Icon={item.icon}
              label={item.label}
              active={view === item.id}
              onClick={() => onNav(item.id)}
              badge={badge}
            />
          );
        })}
      </nav>

    {/* Venture Balance */}
    {ventureBalance && ventureBalance.some(v => v.hours > 0) && (
      <div style={{ borderTop: '1px solid rgba(0,0,0,0.06)', paddingTop: 14, marginBottom: 14 }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: T.textLight, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8, paddingLeft: 4 }}>
          Today
        </div>
        <div style={{ display: 'flex', gap: 3, padding: '0 4px' }}>
          {ventureBalance.filter(v => v.hours > 0).map(v => (
            <div key={v.slug} style={{ flex: v.hours, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
              <div style={{
                height: 4, width: '100%', borderRadius: 2,
                background: v.color, opacity: 0.7,
              }} />
              <span style={{ fontSize: 9, color: T.textMuted, fontWeight: 500 }}>
                {v.hours}h
              </span>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Agent dock — squircle tiles, iOS-style */}
    <div style={{ borderTop: '1px solid rgba(0,0,0,0.06)', paddingTop: 16 }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: T.textLight, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10, paddingLeft: 4 }}>
        Agents
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        {agents.map(a => {
          const colour = a.colour || '#D4921E';
          const initials = a.name.split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
          const active = a.status === 'active';
          return (
            <motion.div
              key={a.id}
              title={`${a.name}${a.last_action ? ` — ${a.last_action}` : ''}`}
              whileHover={{ scale: 1.06, y: -1 }}
              whileTap={{ scale: 0.95 }}
              transition={springs.snappy}
              style={{
                position: 'relative',
                aspectRatio: '1 / 1',
                borderRadius: 11,
                background: `linear-gradient(135deg, ${colour}E6 0%, ${colour} 100%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: '0.02em',
                boxShadow: `0 2px 8px ${colour}40, inset 0 1px 0 rgba(255,255,255,0.4)`,
                opacity: active ? 1 : 0.55,
                cursor: 'help',
              }}
            >
              {initials}
              {active && (
                <span style={{
                  position: 'absolute', top: -2, right: -2,
                  width: 8, height: 8, borderRadius: '50%',
                  background: T.green,
                  boxShadow: '0 0 0 2px rgba(255,255,255,0.85)',
                }} />
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  </div>
  );
};
